import { supabase } from '@/lib/supabase/client';
import { adminClearGuildChatRpc, adminDeleteGuildChatMessageRpc } from '@/lib/supabase/rpc';
import type { Database } from '@/types/database';
import type { EventDiscussionMessage, EventResultAsset, GuildChatMessage } from '@/types/domain';
import { slugify } from '@/utils/text';

type GuildChatRow = Database['public']['Tables']['guild_chat_messages']['Row'];
type EventDiscussionRow = Database['public']['Tables']['event_discussion_messages']['Row'];
type EventResultAssetRow = Database['public']['Tables']['event_result_assets']['Row'];

function assertSupabase() {
  if (!supabase) {
    throw new Error('Supabase is not configured.');
  }

  return supabase;
}

function mapGuildChatMessage(row: GuildChatRow): GuildChatMessage {
  return {
    id: row.id,
    profileId: row.profile_id,
    content: row.content,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapDiscussionMessage(row: EventDiscussionRow): EventDiscussionMessage {
  return {
    id: row.id,
    eventId: row.event_id,
    profileId: row.profile_id,
    content: row.content,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

async function createSignedResultUrl(storageObjectPath: string): Promise<string> {
  const client = assertSupabase();
  const { data, error } = await client.storage.from('event-results').createSignedUrl(storageObjectPath, 60 * 60 * 12);

  if (error || !data?.signedUrl) {
    throw new Error(error?.message ?? 'Failed to create a signed image URL.');
  }

  return data.signedUrl;
}

async function mapResultAsset(row: EventResultAssetRow): Promise<EventResultAsset> {
  return {
    id: row.id,
    eventId: row.event_id,
    uploaderProfileId: row.uploader_profile_id,
    caption: row.caption ?? undefined,
    storageObjectPath: row.storage_object_path,
    imageUrl: await createSignedResultUrl(row.storage_object_path),
    createdAt: row.created_at,
  };
}

export async function listGuildChatMessages(limit = 80): Promise<GuildChatMessage[]> {
  const client = assertSupabase();
  const { data, error } = await client
    .from('guild_chat_messages')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    throw new Error(error.message);
  }

  return (data ?? []).map(mapGuildChatMessage).reverse();
}

export async function sendGuildChatMessage(profileId: string, content: string): Promise<void> {
  const client = assertSupabase();
  const message = content.trim();

  if (!message) {
    return;
  }

  const { error } = await client.from('guild_chat_messages').insert({
    profile_id: profileId,
    content: message,
  });

  if (error) {
    throw new Error(error.message);
  }
}

export async function deleteGuildChatMessage(messageId: string): Promise<void> {
  const { error } = await adminDeleteGuildChatMessageRpc(messageId);

  if (error) {
    throw new Error(error.message);
  }
}

export async function clearGuildChatMessages(): Promise<void> {
  const { error } = await adminClearGuildChatRpc();

  if (error) {
    throw new Error(error.message);
  }
}

export async function listEventDiscussionMessages(eventId: string): Promise<EventDiscussionMessage[]> {
  const client = assertSupabase();
  const { data, error } = await client
    .from('event_discussion_messages')
    .select('*')
    .eq('event_id', eventId)
    .order('created_at', { ascending: true });

  if (error) {
    throw new Error(error.message);
  }

  return (data ?? []).map(mapDiscussionMessage);
}

export async function sendEventDiscussionMessage(eventId: string, profileId: string, content: string): Promise<void> {
  const client = assertSupabase();
  const message = content.trim();

  if (!message) {
    return;
  }

  const { error } = await client.from('event_discussion_messages').insert({
    event_id: eventId,
    profile_id: profileId,
    content: message,
  });

  if (error) {
    throw new Error(error.message);
  }
}

export async function listEventResultAssets(eventId: string): Promise<EventResultAsset[]> {
  const client = assertSupabase();
  const { data, error } = await client
    .from('event_result_assets')
    .select('*')
    .eq('event_id', eventId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(error.message);
  }

  return Promise.all((data ?? []).map((row) => mapResultAsset(row)));
}

export async function uploadEventResultAsset(
  eventId: string,
  uploaderProfileId: string,
  file: File,
  caption?: string,
): Promise<void> {
  const client = assertSupabase();
  const baseName = slugify(file.name.replace(/\.[^.]+$/, '') || 'result');
  const extension = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : 'png';
  const storageObjectPath = `${eventId}/${baseName}-${Date.now()}.${extension}`;
  const { error: uploadError } = await client.storage.from('event-results').upload(storageObjectPath, file, {
    cacheControl: '3600',
    upsert: true,
  });

  if (uploadError) {
    throw new Error(uploadError.message);
  }

  const { error: rowError } = await client.from('event_result_assets').insert({
    event_id: eventId,
    uploader_profile_id: uploaderProfileId,
    caption: caption?.trim() ? caption.trim() : null,
    storage_object_path: storageObjectPath,
  });

  if (rowError) {
    await client.storage.from('event-results').remove([storageObjectPath]).catch(() => {
      // Best-effort cleanup.
    });
    throw new Error(rowError.message);
  }
}

export async function updateEventResultAssetCaption(assetId: string, caption?: string): Promise<void> {
  const client = assertSupabase();
  const { error } = await client
    .from('event_result_assets')
    .update({
      caption: caption?.trim() ? caption.trim() : null,
    })
    .eq('id', assetId);

  if (error) {
    throw new Error(error.message);
  }
}

export async function deleteEventResultAsset(assetId: string): Promise<void> {
  const client = assertSupabase();
  const { data, error } = await client
    .from('event_result_assets')
    .delete()
    .eq('id', assetId)
    .select('storage_object_path')
    .single();

  if (error || !data?.storage_object_path) {
    throw new Error(error?.message ?? 'Failed to delete the result asset.');
  }

  await client.storage.from('event-results').remove([data.storage_object_path]).catch(() => {
    // Best-effort cleanup. The database row is the source of truth for the UI.
  });
}
